import { sum } from 'd3';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Vector3 } from 'three';

import { isValidNumber } from '../../../../../../../utils/math/number';
import { isDefined } from '../../../../../../../utils/variables';
import { PointCloudHandle } from '../../../../../../../viewer/geodata/point-clouds/point-cloud-handle';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { gridFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/generate-grid';
import { InterpolationSnapMode } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/point';
import { loadHighResolutionLodPointCloudChunks } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/point-cloud';
import { getPolygonVertices } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/select';
import { getPolygonArea } from '../../../../../../../viewer/utilities/math-helpers';
import { ViewSettings } from '../../../../../../../viewer/view-settings';
import { useUnitSymbols } from '../../../../../../hooks/use-unit-symbols';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import {
  ProgressDialog,
  useProgressDialogDependencies,
} from '../../../../../progress-dialog/progress-dialog';
import { Select } from '../../../../../select/select';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

const GenerateGrid = (props: Props) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const theme = useTheme();
  const pointCloudAvailable =
    !ViewSettings.instance.isCadViewer && isDefined(PointCloudHandle.instance.activePointCloud);

  const [snapMode, setSnapMode] = React.useState<InterpolationSnapMode>(
    pointCloudAvailable ? 'pointCloud' : 'assets',
  );
  const [gridSize, setGridSize] = React.useState<string>('1');
  const [polygons, setPolygons] = React.useState<Vector3[][]>([]);
  const [area, setArea] = React.useState(0);
  const [height, setHeight] = React.useState(props.tool.snapValues.height.toFixed(2));

  const progress = useProgressDialogDependencies(3);
  const { currentSelection } = useCurrentSelection(props.tool);

  const pointCountWarningTreshold = 15000;
  const minGridSize = 0.02;
  const isInputValid =
    isValidNumber(Number(height)) &&
    isValidNumber(Number(gridSize)) &&
    Number(gridSize) >= minGridSize;
  const pointCountEstimate = isInputValid ? Math.floor(area / Math.pow(Number(gridSize), 2)) : 0;

  const refresh = React.useCallback(() => {
    setPolygons([]);

    const polygonVertices = getPolygonVertices(props.tool, [...currentSelection.lineSegments]);
    const totalArea = sum(polygonVertices.map(getPolygonArea));

    const calculateGridSize = (desiredNumberOfPoints: number) => {
      const size = Math.sqrt(totalArea / desiredNumberOfPoints);
      return size < minGridSize ? minGridSize : size;
    };

    const initialGridSize =
      totalArea > 2000 ? calculateGridSize(2000) : totalArea < 100 ? calculateGridSize(100) : 1;

    setPolygons(polygonVertices);
    setArea(totalArea);
    setGridSize(initialGridSize.toFixed(2));
  }, [props.tool, currentSelection]);

  React.useEffect(refresh, [refresh]);

  const onGenerateGrid = async () => {
    try {
      const pointCloudResult =
        snapMode === 'pointCloud' && PointCloudHandle.instance.activePointCloud
          ? await loadHighResolutionLodPointCloudChunks(
              polygons.flat(),
              progress.controller.current,
              progress.abortController.current,
              PointCloudHandle.instance.activePointCloud,
            )
          : undefined;

      if (progress.abortController.current.signal.aborted) {
        return;
      }

      const result = await gridFunctions.generateGrid(
        props.tool,
        snapMode,
        Number(gridSize),
        Number(height),
        polygons,
        pointCloudResult?.points ?? [],
        progress.abortController.current.signal,
        progress.controller.current,
      );

      for (const child of pointCloudResult?.parent.children ?? []) {
        child.removeFromParent();
      }

      if (progress.abortController.current.signal.aborted) {
        return;
      }

      await gridFunctions.applyGenerateGridResult(props.tool, result).action();
    } catch (err) {
      throw new Error(t('unexpectedError', { ns: 'common' }) as string);
    }
  };

  return (
    <Stack spacing={1}>
      {polygons.length === 0 ? (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.grid.infoNoSelection.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: t('section.functions.grid.infoNoSelection.tooltip.text', {
              ns: 'skyviewDesign',
            }),
          }}
        >
          {t('section.functions.grid.infoNoSelection.text', { ns: 'skyviewDesign' })}
        </InfoBox>
      ) : pointCountEstimate >= pointCountWarningTreshold ? (
        <InfoBox
          color="yellow"
          leftIcon={{ icon: 'exclamation-triangle' }}
          title={t('section.functions.grid.infoSelectionTresholdExceeded.title', {
            ns: 'skyviewDesign',
          })}
        >
          <Trans
            components={{ br: <br />, bold: <strong /> }}
            i18nKey="section.functions.grid.infoSelectionTresholdExceeded.text"
            ns="skyviewDesign"
            values={{
              count: pointCountEstimate,
              threshold: pointCountWarningTreshold,
            }}
          />
        </InfoBox>
      ) : (
        <InfoBox
          color="yellow"
          title={t('section.functions.grid.infoSelectionTresholdNotExceeded.title', {
            ns: 'skyviewDesign',
          })}
        >
          <Trans
            components={{ br: <br />, bold: <strong /> }}
            i18nKey="section.functions.grid.infoSelectionTresholdNotExceeded.text"
            ns="skyviewDesign"
            values={{ count: pointCountEstimate }}
          />
        </InfoBox>
      )}

      {polygons.length > 0 && (
        <>
          <LabelledContainer text={t('section.snap.title', { ns: 'skyviewDesign' })}>
            <Select
              options={[
                ...(pointCloudAvailable
                  ? [
                      {
                        id: 'pointCloud',
                        name: t('snap.pointCloud', {
                          ns: 'skyviewDesign',
                        }),
                      },
                    ]
                  : []),
                {
                  id: 'assets',
                  name: t('snap.assets', {
                    ns: 'skyviewDesign',
                  }),
                },
                {
                  id: 'height',
                  name: t('snap.height', {
                    ns: 'skyviewDesign',
                  }),
                },
              ]}
              value={snapMode}
              onChange={(e) => setSnapMode(e.target.value as InterpolationSnapMode)}
            />
          </LabelledContainer>

          <LabelledContainer
            text={t('section.functions.grid.settings.distanceText', {
              ns: 'skyviewDesign',
              unit: unitSymbols.length,
            })}
          >
            <TextBox
              errorMessage={
                isInputValid
                  ? undefined
                  : t('section.functions.grid.settings.distanceErrorMessage', {
                      minGridSize,
                      ns: 'skyviewDesign',
                    })
              }
              min={'minGridSize'}
              type={'number'}
              value={gridSize}
              onChange={(e) => setGridSize(e.target.value)}
            />
          </LabelledContainer>

          {snapMode === 'height' && (
            <LabelledContainer text={t('snap.height', { ns: 'skyviewDesign' })}>
              <TextBox type={'number'} value={height} onChange={(e) => setHeight(e.target.value)} />
            </LabelledContainer>
          )}
        </>
      )}

      <Button
        color={'primary'}
        disabled={polygons.length === 0 || !isInputValid}
        variant="contained"
        onClick={() => progress.setShowDialog(true)}
      >
        {t('start', { ns: 'common' })}
      </Button>

      {progress.showDialog && (
        <ProgressDialog
          abortController={progress.abortController.current}
          callback={onGenerateGrid}
          progressController={progress.controller.current}
          title={t('section.functions.grid.saveDialog.title', { ns: 'skyviewDesign' })}
          onClose={() => {
            progress.setShowDialog(false);
          }}
        />
      )}
    </Stack>
  );
};

export { GenerateGrid };
